import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  LogOut,
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Copy,
  Search,
  Bell,
  ArrowRightLeft,
  History,
  X,
  Eye,
  EyeOff,
  Filter,
  Download,
  Upload,
  ChevronLeft,
  ChevronRight,
  CreditCard
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { QRCodeSVG } from 'qrcode.react';

const WalletPage = () => {
  const { user, logout } = useAuthStore();
  const [balances, setBalances] = useState([]);
  const [supportedTokens, setSupportedTokens] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({ email: '', amount: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [depositAddress, setDepositAddress] = useState('');
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [withdrawData, setWithdrawData] = useState({ address: '', amount: '' });
  const [showBalance, setShowBalance] = useState(true);
  const [hideZeroBalance, setHideZeroBalance] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [transferType, setTransferType] = useState('internal'); // 'internal', 'user', 'p2p'
  const [internalTransferDirection, setInternalTransferDirection] = useState({ from: 'spot', to: 'futures' });

  // P2P & INR State
  const [activeTab, setActiveTab] = useState('spot'); // 'spot', 'futures', 'p2p', 'inr'
  const [p2pBalances, setP2PBalances] = useState([]);
  const [fiatWallet, setFiatWallet] = useState(null);
  const [showInrDepositModal, setShowInrDepositModal] = useState(false);
  const [inrDepositData, setInrDepositData] = useState({ amount: '', referenceId: '' });

  // Pagination State
  const [balancePage, setBalancePage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const [transactionPage, setTransactionPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Recipient Lookup State
  const [recipientName, setRecipientName] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [isCheckingRecipient, setIsCheckingRecipient] = useState(false);

  useEffect(() => {
    const checkRecipient = async () => {
      if (!transferData.email || !transferData.email.includes('@')) {
        setRecipientName('');
        setRecipientError('');
        return;
      }

      setIsCheckingRecipient(true);
      setRecipientError('');
      setRecipientName('');

      try {
        const res = await api.get(`/wallet/lookup-user?email=${transferData.email}`);
        setRecipientName(`${res.data.firstName} ${res.data.lastName}`);
      } catch (error) {
        setRecipientError('User not available');
      } finally {
        setIsCheckingRecipient(false);
      }
    };

    const timeoutId = setTimeout(checkRecipient, 500);
    return () => clearTimeout(timeoutId);
  }, [transferData.email]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTransactions(transactionPage);
  }, [transactionPage, transactionFilter]);

  const fetchData = async () => {
    try {
      const [balancesRes, tokensRes, p2pRes, fiatRes] = await Promise.all([
        api.get('/wallet/balances'),
        api.get('/wallet/tokens'),
        api.get('/p2p/balance'),
        api.get('/fiat/wallet')
      ]);
      setBalances(balancesRes.data.balances);
      setSupportedTokens(tokensRes.data.tokens);
      setP2PBalances(p2pRes.data.balances);
      setFiatWallet(fiatRes.data.wallet);

      // Initial transaction fetch
      await fetchTransactions(1);
    } catch (error) {
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async (page) => {
    setLoadingTransactions(true);
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      let url = `/wallet/transactions?limit=${ITEMS_PER_PAGE}&offset=${offset}`;
      if (transactionFilter !== 'all') {
        url += `&type=${transactionFilter}`;
      }

      const res = await api.get(url);
      setTransactions(res.data.transactions);
      setTotalTransactions(res.data.total || 0); // Assuming backend returns total count, if not we might need to adjust
    } catch (error) {
      console.error('Failed to fetch transactions', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      if (transferType === 'user') {
        await api.post('/wallet/transfer', {
          tokenId: selectedToken.id,
          amount: parseFloat(transferData.amount),
          recipientEmail: transferData.email
        });
      } else if (transferType === 'internal') {
        await api.post('/wallet/transfer-internal', {
          symbol: selectedToken.symbol,
          amount: parseFloat(transferData.amount),
          fromType: internalTransferDirection.from,
          toType: internalTransferDirection.to
        });
      } else if (transferType === 'p2p') {
        await api.post('/p2p/transfer', {
          tokenId: selectedToken.id,
          amount: parseFloat(transferData.amount),
          type: internalTransferDirection.from === 'spot' ? 'spot_to_p2p' : 'p2p_to_spot'
        });
      }
      toast.success('Transfer successful');
      setShowTransferModal(false);
      setTransferData({ email: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Transfer failed');
    }
  };

  const handleInrDeposit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/fiat/deposit', {
        amount: parseFloat(inrDepositData.amount),
        referenceId: inrDepositData.referenceId
      });
      toast.success('Deposit request submitted. Waiting for admin approval.');
      setShowInrDepositModal(false);
      setInrDepositData({ amount: '', referenceId: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Deposit failed');
    }
  };

  const handleDeposit = async (tokenId) => {
    try {
      const res = await api.get(`/wallet/deposit/${tokenId}`);
      const { addresses, token } = res.data.depositInfo;

      setSelectedToken(token);

      // Use networks configured by admin (already filtered by depositEnabled on backend)
      const configuredNetworks = addresses || [];

      setAvailableNetworks(configuredNetworks);

      if (configuredNetworks.length > 0) {
        // Default to first network
        setSelectedNetwork(configuredNetworks[0]);
        setDepositAddress(configuredNetworks[0].address);
      } else {
        // No networks configured by admin = deposits not enabled
        setDepositAddress('');
        setAvailableNetworks([]);
        setSelectedNetwork(null);
      }

      setShowDepositModal(true);
    } catch (error) {
      toast.error('Failed to get deposit address');
    }
  };

  const handleNetworkChange = (networkId) => {
    const network = availableNetworks.find(n => n.networkId === networkId);
    if (network) {
      setSelectedNetwork(network);
      setDepositAddress(network.address);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    try {
      await api.post('/wallet/withdraw', {
        tokenId: selectedToken.id,
        amount: parseFloat(withdrawData.amount),
        address: withdrawData.address
      });
      toast.success('Withdrawal request submitted');
      setShowWithdrawModal(false);
      setWithdrawData({ address: '', amount: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Withdrawal failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  // Filter and Paginate Assets
  const filteredAssets = supportedTokens.map(token => {
    const balance = balances.find(b => b.token && b.token.id === token.id);
    return {
      id: token.id,
      token: token,
      balance: balance ? balance.balance : '0',
      lockedBalance: balance ? balance.lockedBalance : '0',
      futuresBalance: balance ? balance.futuresBalance : '0',
      futuresLockedBalance: balance ? balance.futuresLockedBalance : '0'
    };
  }).filter(asset => {
    // Hide Zero Balance Filter
    if (hideZeroBalance) {
      const total = parseFloat(asset.balance) + parseFloat(asset.lockedBalance) + parseFloat(asset.futuresBalance) + parseFloat(asset.futuresLockedBalance);
      if (total <= 0) return false;
    }

    if (!searchTerm) return true;
    return (
      asset.token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.token.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalBalancePages = Math.ceil(filteredAssets.length / ITEMS_PER_PAGE);
  const paginatedAssets = filteredAssets.slice(
    (balancePage - 1) * ITEMS_PER_PAGE,
    balancePage * ITEMS_PER_PAGE
  );

  const totalBalance = balances.reduce((sum, b) => {
    if (!b.token) return sum;
    const price = b.token.symbol === 'USDT' ? 1 : 0; // Simplified

    let amount = 0;
    if (activeTab === 'futures') {
      amount = parseFloat(b.futuresBalance);
    } else if (activeTab === 'p2p') {
      // Find matching P2P balance
      const p2pAsset = p2pBalances.find(p => p.tokenId === b.token.id);
      amount = p2pAsset ? parseFloat(p2pAsset.balance) : 0;
    } else if (activeTab === 'inr') {
      // INR handled separately in render, but if we want it in total header:
      // We can return sum, as INR is fiat, not crypto token list. 
      // Or we can just show 0 or handle it if b.token is INR (which it isn't).
      amount = 0;
    } else {
      amount = parseFloat(b.balance);
    }

    return sum + (amount * price);
  }, 0);

  const openModal = (type, token = null) => {
    if (token) {
      setSelectedToken(token);
    } else if (supportedTokens.length > 0 && !selectedToken) {
      setSelectedToken(supportedTokens[0]);
    }

    if (type === 'deposit') {
      if (token) handleDeposit(token.id);
      else setShowDepositModal(true);
    } else if (type === 'withdraw') {
      setShowWithdrawModal(true);
    } else if (type === 'transfer') {
      setShowTransferModal(true);
    }
  };

  // Helper to render token select options
  const renderTokenOptions = () => (
    <div className="mb-4">
      <label className="block text-sm text-gray-400 mb-2">Select Asset</label>
      <select
        value={selectedToken?.id || ''}
        onChange={(e) => {
          const selectedValue = e.target.value;
          // Try to find by string comparison first, then by number comparison
          let token = supportedTokens.find(t => String(t.id) === selectedValue);
          if (!token) {
            token = supportedTokens.find(t => t.id === parseInt(selectedValue));
          }
          setSelectedToken(token);
          if (showDepositModal && token) handleDeposit(token.id);
        }}
        className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
      >
        <option value="">Select a token</option>
        {supportedTokens.map(token => (
          <option key={token.id} value={token.id}>
            {token.symbol} - {token.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans selection:bg-blue-500/30">
      <Navbar />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">My Wallet</h1>
        </div>

        {/* Total Balance Card */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-900 rounded-3xl p-8 mb-8 relative overflow-hidden shadow-2xl shadow-blue-900/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-blue-100/80">
                <Wallet size={20} />
                <span className="text-sm font-medium tracking-wide uppercase">TOTAL {activeTab} BALANCE</span>
              </div>
              <button onClick={() => setShowBalance(!showBalance)} className="text-white/60 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg">
                {showBalance ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
            <div className="text-6xl font-bold text-white mb-8 tracking-tight">
              {showBalance ? `$${totalBalance.toFixed(2)}` : '••••••••'}
              <span className="text-2xl text-blue-200/60 font-medium ml-2">USD</span>
            </div>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <button onClick={() => openModal('deposit')} className="flex items-center gap-2 bg-white text-blue-900 px-5 py-2.5 sm:px-8 sm:py-3.5 rounded-xl font-bold hover:bg-blue-50 transition-all shadow-lg shadow-black/20 hover:scale-105 active:scale-95 text-sm sm:text-base">
                <Download size={20} />
                Deposit
              </button>
              <button onClick={() => openModal('withdraw')} className="flex items-center gap-2 bg-white/10 text-white px-5 py-2.5 sm:px-8 sm:py-3.5 rounded-xl font-bold hover:bg-white/20 transition-all backdrop-blur-md border border-white/10 hover:scale-105 active:scale-95 text-sm sm:text-base">
                <Upload size={20} />
                Withdraw
              </button>
              <button onClick={() => openModal('transfer')} className="flex items-center gap-2 bg-white/10 text-white px-5 py-2.5 sm:px-8 sm:py-3.5 rounded-xl font-bold hover:bg-white/20 transition-all backdrop-blur-md border border-white/10 hover:scale-105 active:scale-95 text-sm sm:text-base">
                <ArrowRightLeft size={20} />
                Transfer
              </button>
            </div>
          </div>
        </div >

        {/* Tabs */}
        < div className="flex gap-2 sm:gap-4 mb-6 overflow-x-auto pb-2" >
          {
            ['spot', 'futures', 'p2p', 'inr'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wide transition-all whitespace-nowrap ${activeTab === tab
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                  : 'bg-[#181a20] text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
              >
                {tab} Wallet
              </button>
            ))
          }
        </div >

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Balances Section */}
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-[#181a20] rounded-3xl border border-gray-800/50 overflow-hidden shadow-xl">
              <div className="p-6 border-b border-gray-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <CreditCard className="text-blue-500" size={24} />
                  Assets
                </h2>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="hideZeroBalance"
                      checked={hideZeroBalance}
                      onChange={(e) => setHideZeroBalance(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-700 bg-[#0b0e11] text-blue-600 focus:ring-blue-500/50 focus:ring-offset-0"
                    />
                    <label htmlFor="hideZeroBalance" className="text-sm text-gray-400 cursor-pointer select-none hover:text-white transition-colors">
                      Hide zero balance
                    </label>
                  </div>
                  <div className="flex items-center gap-2 bg-[#0b0e11] border border-gray-700/50 px-4 py-2.5 rounded-xl focus-within:border-blue-500/50 transition-colors">
                    <Search size={18} className="text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setBalancePage(1);
                      }}
                      className="bg-transparent border-none focus:outline-none text-white placeholder-gray-500 w-full sm:w-48 text-sm"
                    />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-3">
                  <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  <span className="text-sm font-medium">Loading assets...</span>
                </div>
              ) : activeTab === 'spot' || activeTab === 'futures' ? (
                paginatedAssets.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider border-b border-gray-800/50 bg-gray-800/20">
                            <th className="px-6 py-4">Asset</th>
                            {activeTab === 'spot' && <th className="px-6 py-4 text-right">Spot Balance</th>}
                            {activeTab === 'futures' && <th className="px-6 py-4 text-right">Futures Balance</th>}
                            <th className="px-6 py-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                          {paginatedAssets.map((asset) => (
                            <tr key={asset.id} className="hover:bg-white/[0.02] transition-colors group">
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center text-sm font-bold text-white shadow-lg border border-white/5">
                                    {asset.token.symbol[0]}
                                  </div>
                                  <div>
                                    <div className="font-bold text-white group-hover:text-blue-400 transition-colors">{asset.token.symbol}</div>
                                    <div className="text-xs text-gray-500 font-medium">{asset.token.name}</div>
                                  </div>
                                </div>
                              </td>
                              {activeTab === 'spot' && (
                                <td className="px-6 py-4 text-right">
                                  <div className="font-bold text-white font-mono tracking-tight">
                                    {parseFloat(asset.balance).toFixed(6)}
                                  </div>
                                  <div className="text-xs text-gray-500 font-medium">
                                    Locked: <span className="text-gray-400">{parseFloat(asset.lockedBalance).toFixed(6)}</span>
                                  </div>
                                </td>
                              )}
                              {activeTab === 'futures' && (
                                <td className="px-6 py-4 text-right">
                                  <div className="font-bold text-white font-mono tracking-tight">
                                    {parseFloat(asset.futuresBalance).toFixed(6)}
                                  </div>
                                  <div className="text-xs text-gray-500 font-medium">
                                    Locked: <span className="text-gray-400">{parseFloat(asset.futuresLockedBalance).toFixed(6)}</span>
                                  </div>
                                </td>
                              )}
                              <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => openModal('deposit', asset.token)}
                                    disabled={!asset.token.depositEnabled}
                                    className={`px-4 py-2 rounded-lg transition-all text-xs font-bold uppercase tracking-wide
                                      ${asset.token.depositEnabled
                                        ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white'
                                        : 'bg-gray-800 text-gray-600 cursor-not-allowed'}`}
                                  >
                                    {asset.token.depositEnabled ? 'Deposit' : 'Disabled'}
                                  </button>
                                  {parseFloat(asset.balance) > 0 && (
                                    <button
                                      onClick={() => openModal('withdraw', asset.token)}
                                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all"
                                      title="Withdraw"
                                    >
                                      <Upload size={16} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalBalancePages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-800/50 flex items-center justify-between bg-gray-800/20">
                        <div className="text-sm text-gray-500">
                          Showing <span className="font-medium text-white">{(balancePage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium text-white">{Math.min(balancePage * ITEMS_PER_PAGE, filteredAssets.length)}</span> of <span className="font-medium text-white">{filteredAssets.length}</span> assets
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setBalancePage(p => Math.max(1, p - 1))}
                            disabled={balancePage === 1}
                            className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          <button
                            onClick={() => setBalancePage(p => Math.min(totalBalancePages, p + 1))}
                            disabled={balancePage === totalBalancePages}
                            className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-2">
                    <Search size={48} className="text-gray-700 mb-2" />
                    <p className="text-lg font-medium">No assets found</p>
                    <p className="text-sm">Try adjusting your search terms</p>
                  </div>
                )
              ) : activeTab === 'p2p' ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-xs font-semibold uppercase tracking-wider border-b border-gray-800/50 bg-gray-800/20">
                        <th className="px-6 py-4">Asset</th>
                        <th className="px-6 py-4 text-right">P2P Balance</th>
                        <th className="px-6 py-4 text-right">Locked</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {p2pBalances.length > 0 ? p2pBalances.map((asset) => (
                        <tr key={asset.tokenId} className="hover:bg-white/[0.02] transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-700 to-purple-900 flex items-center justify-center text-sm font-bold text-white shadow-lg border border-white/5">
                                {asset.symbol[0]}
                              </div>
                              <div>
                                <div className="font-bold text-white">{asset.symbol}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right font-mono font-bold text-white">
                            {parseFloat(asset.balance).toFixed(6)}
                          </td>
                          <td className="px-6 py-4 text-right font-mono text-gray-400">
                            {parseFloat(asset.lockedBalance).toFixed(6)}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="3" className="px-6 py-20 text-center text-gray-500">
                            No P2P assets found. Transfer funds from Spot wallet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                // INR Wallet View
                <div className="p-8 flex flex-col items-center justify-center text-center">
                  <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                    <span className="text-3xl font-bold text-green-500">₹</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">INR Wallet</h3>
                  <div className="text-4xl font-bold text-white mb-8">
                    ₹{fiatWallet ? parseFloat(fiatWallet.balance).toFixed(2) : '0.00'}
                  </div>
                  <button
                    onClick={() => setShowInrDepositModal(true)}
                    className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-green-900/20"
                  >
                    Deposit INR
                  </button>
                  <p className="mt-6 text-sm text-gray-500 max-w-md">
                    Deposit INR to your wallet via bank transfer. Once approved by admin, funds will be credited to your account.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Transaction History */}
          <div className="bg-[#181a20] rounded-3xl border border-gray-800/50 overflow-hidden shadow-xl flex flex-col h-auto sm:h-[800px]">
            <div className="p-6 border-b border-gray-800/50 flex-shrink-0">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <History className="text-blue-500" size={24} />
                Recent History
              </h2>

              <div className="flex flex-wrap gap-2">
                {['all', 'deposit', 'withdraw', 'transfer_in', 'transfer_out', 'internal_transfer', 'bot_subscription', 'bot_investment'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => {
                      setTransactionFilter(filter);
                      setTransactionPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${transactionFilter === filter
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50'
                      : 'bg-[#0b0e11] text-gray-400 hover:text-white hover:bg-gray-800'
                      }`}
                  >
                    {filter === 'bot_subscription' ? 'Bot Fee' : filter === 'bot_investment' ? 'Bot Invest' : filter.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {loadingTransactions ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-3">
                  <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                  <span className="text-sm font-medium">Loading history...</span>
                </div>
              ) : transactions.length > 0 ? (
                <div className="divide-y divide-gray-800/50">
                  {transactions.map((tx) => {
                    const isIncoming = tx.type === 'deposit' || tx.type === 'transfer_in' || tx.type === 'bot_profit' || (tx.type === 'internal_transfer' && tx.address && tx.address.includes('-> spot'));
                    const isBot = tx.type === 'bot_subscription' || tx.type === 'bot_investment' || tx.type === 'bot_profit';
                    const getTypeLabel = () => {
                      if (tx.type === 'bot_subscription') return 'Bot Subscription Fee';
                      if (tx.type === 'bot_investment') return 'Bot Investment';
                      if (tx.type === 'bot_profit') return 'Bot Profit';
                      return tx.type.replace(/_/g, ' ');
                    };
                    return (
                      <div key={tx.id} className="p-5 hover:bg-white/[0.02] transition-colors group">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${isBot
                              ? 'bg-purple-500/10 text-purple-500 border border-purple-500/20'
                              : isIncoming
                                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                : 'bg-red-500/10 text-red-500 border border-red-500/20'
                              }`}>
                              {isIncoming
                                ? <ArrowDownCircle size={20} />
                                : <ArrowUpCircle size={20} />
                              }
                            </div>
                            <div>
                              <div className="font-bold text-white capitalize text-sm">{getTypeLabel()}</div>
                              <div className="text-xs text-gray-500 font-medium mt-0.5">{tx.token.symbol}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`font-bold font-mono tracking-tight ${isBot
                              ? 'text-purple-400'
                              : isIncoming
                                ? 'text-green-500'
                                : 'text-red-500'
                              }`}>
                              {isIncoming ? '+' : '-'}{parseFloat(tx.amount).toFixed(6)}
                            </div>
                            <div className={`text-[10px] font-bold uppercase tracking-wider mt-1 ${tx.status === 'completed' ? 'text-green-500' :
                              tx.status === 'pending' ? 'text-yellow-500' : 'text-red-500'
                              }`}>
                              {tx.status}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600 pl-14 font-medium">
                          {new Date(tx.createdAt).toLocaleString()}
                          {tx.notes && (() => {
                            try {
                              const parsedNotes = JSON.parse(tx.notes);
                              if (parsedNotes.action === 'admin_credit' || parsedNotes.action === 'admin_debit') {
                                return <span className="ml-2 text-gray-500">• {parsedNotes.reason || 'Admin adjustment'}</span>;
                              }
                              return <span className="ml-2 text-gray-500">• {tx.notes}</span>;
                            } catch {
                              return <span className="ml-2 text-gray-500">• {tx.notes}</span>;
                            }
                          })()}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 gap-2">
                  <History size={32} className="text-gray-700 mb-2" />
                  <p className="text-sm font-medium">No transactions found</p>
                </div>
              )}
            </div>

            {/* Pagination for Transactions */}
            <div className="p-4 border-t border-gray-800/50 bg-gray-800/20 flex items-center justify-between flex-shrink-0">
              <button
                onClick={() => setTransactionPage(p => Math.max(1, p - 1))}
                disabled={transactionPage === 1}
                className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs font-medium text-gray-500">Page {transactionPage}</span>
              <button
                onClick={() => setTransactionPage(p => p + 1)}
                disabled={transactions.length < ITEMS_PER_PAGE}
                className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </main >

      {/* Deposit Modal - Compact Professional */}
      {showDepositModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#181a20] rounded-2xl w-full max-w-sm shadow-2xl border border-gray-800">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-white">Deposit</span>
                <span className="text-lg text-blue-500 font-semibold">{selectedToken?.symbol}</span>
              </div>
              <button onClick={() => setShowDepositModal(false)} className="text-gray-400 hover:text-white p-1">
                <X size={20} />
              </button>
            </div>

            {!selectedToken && <div className="p-4">{renderTokenOptions()}</div>}

            {selectedToken && availableNetworks.length > 0 && (
              <div className="p-4 space-y-4">
                {/* Network Pills */}
                <div className="flex flex-wrap gap-2">
                  {availableNetworks.map(net => (
                    <button
                      key={net.networkId}
                      onClick={() => handleNetworkChange(net.networkId)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedNetwork?.networkId === net.networkId
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#2b2f36] text-gray-300 hover:bg-gray-700'
                        }`}
                    >
                      {net.networkSymbol}
                    </button>
                  ))}
                </div>

                {/* QR + Address */}
                <div className="bg-[#2b2f36] rounded-xl p-4">
                  <div className="flex justify-center mb-3">
                    <div className="bg-white p-2 rounded-lg">
                      <QRCodeSVG value={depositAddress} size={120} level="M" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-[#181a20] rounded-lg p-2 border border-gray-700">
                    <p className="flex-1 text-xs text-gray-300 font-mono truncate">{depositAddress}</p>
                    <button
                      onClick={() => copyToClipboard(depositAddress)}
                      className="p-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white transition-colors"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>

                {/* Warning */}
                <p className="text-xs text-gray-500 text-center">
                  Send only <span className="text-blue-400 font-medium">{selectedToken?.symbol}</span> via{' '}
                  <span className="text-blue-400 font-medium">{selectedNetwork?.networkSymbol}</span> network
                </p>
              </div>
            )}

            {selectedToken && availableNetworks.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-red-400 text-sm">Deposits not available for {selectedToken?.symbol}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {
        showWithdrawModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 max-w-md w-full shadow-2xl scale-100 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold text-white">Withdraw</h3>
                <button onClick={() => setShowWithdrawModal(false)} className="text-gray-400 hover:text-white transition-colors bg-gray-800/50 p-2 rounded-full hover:bg-gray-800">
                  <X size={20} />
                </button>
              </div>

              {renderTokenOptions()}

              <form onSubmit={handleWithdraw} className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Withdrawal Address</label>
                  <input
                    type="text"
                    value={withdrawData.address}
                    onChange={(e) => setWithdrawData({ ...withdrawData, address: e.target.value })}
                    className="w-full px-4 py-3.5 bg-[#0b0e11] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
                    placeholder="Enter destination address"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Amount</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.000001"
                      value={withdrawData.amount}
                      onChange={(e) => setWithdrawData({ ...withdrawData, amount: e.target.value })}
                      className="w-full px-4 py-3.5 bg-[#0b0e11] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors pr-16 font-mono"
                      placeholder="0.00"
                      required
                    />
                    <span className="absolute right-4 top-4 text-gray-500 font-bold">{selectedToken?.symbol}</span>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Confirm Withdrawal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Transfer Modal */}
      {
        showTransferModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 max-w-md w-full shadow-2xl scale-100 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold text-white">Transfer</h3>
                <button onClick={() => setShowTransferModal(false)} className="text-gray-400 hover:text-white transition-colors bg-gray-800/50 p-2 rounded-full hover:bg-gray-800">
                  <X size={20} />
                </button>
              </div>

              {/* Transfer Type Tabs */}
              <div className="flex gap-2 mb-8 bg-[#0b0e11] p-1.5 rounded-xl">
                <button
                  onClick={() => setTransferType('internal')}
                  className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${transferType === 'internal'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                >
                  Spot ↔ Futures
                </button>
                <button
                  onClick={() => setTransferType('user')}
                  className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${transferType === 'user'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                >
                  Send to User
                </button>
                <button
                  onClick={() => setTransferType('p2p')}
                  className={`flex-1 py-2.5 rounded-lg font-bold text-sm transition-all ${transferType === 'p2p'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                >
                  Spot ↔ P2P
                </button>
              </div>

              {renderTokenOptions()}

              {/* Available Balance Display - shown above the form */}
              {selectedToken && (() => {
                // Find the asset from filteredAssets which already has properly mapped balance data
                const asset = filteredAssets.find(a => String(a.token.id) === String(selectedToken.id));

                if (!asset) return null;

                let availableBalance = 0;
                let balanceLabel = '';

                if (transferType === 'internal') {
                  if (internalTransferDirection.from === 'spot') {
                    availableBalance = parseFloat(asset.balance || 0) - parseFloat(asset.lockedBalance || 0);
                    balanceLabel = 'Spot Balance';
                  } else {
                    availableBalance = parseFloat(asset.futuresBalance || 0) - parseFloat(asset.futuresLockedBalance || 0);
                    balanceLabel = 'Futures Balance';
                  }
                } else if (transferType === 'p2p') {
                  if (internalTransferDirection.from === 'spot') {
                    availableBalance = parseFloat(asset.balance || 0) - parseFloat(asset.lockedBalance || 0);
                    balanceLabel = 'Spot Balance';
                  } else {
                    // For P2P balance, find from p2pBalances
                    const p2pAsset = p2pBalances.find(b => String(b.tokenId) === String(selectedToken.id));
                    availableBalance = p2pAsset ? parseFloat(p2pAsset.balance || 0) - parseFloat(p2pAsset.lockedBalance || 0) : 0;
                    balanceLabel = 'P2P Balance';
                  }
                } else if (transferType === 'user') {
                  availableBalance = parseFloat(asset.balance || 0) - parseFloat(asset.lockedBalance || 0);
                  balanceLabel = 'Available Balance';
                }

                const displayBalance = isNaN(availableBalance) ? 0 : Math.max(0, availableBalance);

                return (
                  <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-xl px-4 py-3 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{balanceLabel}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-white font-mono">{displayBalance.toFixed(4)}</span>
                        <span className="text-sm font-bold text-blue-400">{selectedToken.symbol}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <form onSubmit={handleTransfer} className="space-y-6">
                {transferType === 'internal' ? (
                  <div className="flex items-center gap-4 bg-[#0b0e11] p-6 rounded-2xl border border-gray-700/50">
                    <div className="flex-1 text-center">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">From</div>
                      <div className="font-bold text-white uppercase text-lg">{internalTransferDirection.from}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInternalTransferDirection(prev => ({ from: prev.to, to: prev.from }))}
                      className="p-3 bg-gray-800 rounded-full hover:bg-gray-700 transition-all border border-gray-700 text-blue-400"
                    >
                      <ArrowRightLeft size={20} />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">To</div>
                      <div className="font-bold text-white uppercase text-lg">{internalTransferDirection.to}</div>
                    </div>
                  </div>
                ) : transferType === 'p2p' ? (
                  <div className="flex items-center gap-4 bg-[#0b0e11] p-6 rounded-2xl border border-gray-700/50">
                    <div className="flex-1 text-center">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">From</div>
                      <div className="font-bold text-white uppercase text-lg">{internalTransferDirection.from === 'spot' ? 'SPOT' : 'P2P'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInternalTransferDirection(prev => ({ from: prev.from === 'spot' ? 'p2p' : 'spot', to: prev.to === 'spot' ? 'p2p' : 'spot' }))}
                      className="p-3 bg-gray-800 rounded-full hover:bg-gray-700 transition-all border border-gray-700 text-blue-400"
                    >
                      <ArrowRightLeft size={20} />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">To</div>
                      <div className="font-bold text-white uppercase text-lg">{internalTransferDirection.from === 'spot' ? 'P2P' : 'SPOT'}</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Recipient Email</label>
                    <input
                      type="email"
                      value={transferData.email}
                      onChange={(e) => setTransferData({ ...transferData, email: e.target.value })}
                      className={`w-full px-4 py-3.5 bg-[#0b0e11] border ${recipientError ? 'border-red-500' : recipientName ? 'border-green-500' : 'border-gray-700'} rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors`}
                      placeholder="Enter recipient email"
                      required
                    />
                    {isCheckingRecipient && <p className="text-xs text-gray-500 mt-2 font-medium">Checking...</p>}
                    {recipientName && <p className="text-xs text-green-500 mt-2 font-bold">✓ {recipientName}</p>}
                    {recipientError && <p className="text-xs text-red-500 mt-2 font-bold">✗ {recipientError}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Amount</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.000001"
                      value={transferData.amount}
                      onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                      className="w-full px-4 py-3.5 bg-[#0b0e11] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors pr-16 font-mono"
                      placeholder="0.00"
                      required
                    />
                    <span className="absolute right-4 top-4 text-gray-500 font-bold">{selectedToken?.symbol}</span>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/20 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Confirm Transfer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* INR Deposit Modal */}
      {
        showInrDepositModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 max-w-md w-full shadow-2xl scale-100 animate-in zoom-in-95 duration-200">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-bold text-white">Deposit INR</h3>
                <button onClick={() => setShowInrDepositModal(false)} className="text-gray-400 hover:text-white transition-colors bg-gray-800/50 p-2 rounded-full hover:bg-gray-800">
                  <X size={20} />
                </button>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-bold text-blue-400 mb-2 uppercase tracking-wide">Bank Details</h4>
                <div className="space-y-1 text-sm text-gray-300">
                  <p><span className="text-gray-500">Bank Name:</span> HDFC Bank</p>
                  <p><span className="text-gray-500">Account Name:</span> Albatech Pvt Ltd</p>
                  <p><span className="text-gray-500">Account No:</span> 1234567890</p>
                  <p><span className="text-gray-500">IFSC Code:</span> HDFC0001234</p>
                </div>
              </div>

              <form onSubmit={handleInrDeposit} className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Amount (INR)</label>
                  <input
                    type="number"
                    value={inrDepositData.amount}
                    onChange={(e) => setInrDepositData({ ...inrDepositData, amount: e.target.value })}
                    className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="Enter amount"
                    required
                    min="100"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">UTR / Reference ID</label>
                  <input
                    type="text"
                    value={inrDepositData.referenceId}
                    onChange={(e) => setInrDepositData({ ...inrDepositData, referenceId: e.target.value })}
                    className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="Enter transaction UTR"
                    required
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-blue-900/20 active:scale-95"
                  >
                    Submit Deposit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default WalletPage;
